/**
 * 판정기(assistant_judge) 자막 접기 설정 — 이 브라우저의 취향(localStorage,
 * use-collapse-inferred.ts 와 같은 규율). 서버 왕복 없음.
 *
 * **기존 훅에 합류시키지 않는다.** 축이 둘인데 손잡이가 하나면, 한쪽을
 * 끄려다 다른 쪽이 같이 꺼지고 사후에 **어느 축이 접었는지 못 가른다**
 * (`inferred` 를 표시 플래그로 재사용하면 안 되는 것과 같은 이유).
 */

import { useCallback, useEffect, useState } from "react";
import type { FeedItem } from "./reducer";

const STORAGE_KEY = "omnitrans:collapse-assistant";

/**
 * 저장값 해석 — 저장된 값이 없으면 기본값은 **접힘(on)** 이다
 * (사용자 결정 2026-09-19: "난 접는걸 기본으로 원해").
 *
 * 추론 접기(`use-collapse-inferred.ts:17`)와 **같은 규율**이다:
 * 미저장(`null`)이면 on, 저장돼 있으면 그 값을 그대로 따른다.
 * 이 형태라야 **사용자가 끈 선택이 보존된다** — `raw !== '0'` 처럼 쓰면
 * 미저장과 `'0'` 을 갈라도 다른 저장값에서 어긋나고, `raw === '1'` 로만
 * 쓰면 미저장이 off 가 되어 기본값 결정이 뒤집힌다.
 *
 * 잔여 위험: 판정기 정확도는 **단발 측정**이다(§2 3회 반복 미충족).
 * 오판정이 나면 정상 발화가 기본으로 접힌다 — 접힘은 유실이 아니라
 * 한 줄로 남고 펼치면 원문 그대로지만(§1-1), FP 가 늘면 이 기본값을
 * 다시 본다.
 */
function parseCollapse(raw: unknown): boolean {
  return raw === null ? true : raw === "1";
}

export function useCollapseAssistant(): {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  toggle: () => void;
} {
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    if (typeof localStorage === "undefined") return true;
    try {
      return parseCollapse(localStorage.getItem(STORAGE_KEY));
    } catch {
      return true;
    }
  });

  /** 다른 탭의 토글을 따라간다 — storage 이벤트는 **다른 문서**의 쓰기에만
   *  오므로 자기 토글과 겹치지 않는다. */
  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (e.key === STORAGE_KEY) setCollapsedState(parseCollapse(e.newValue));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch {
      // 저장 실패는 무시한다 — 접기 설정은 이번 세션만 유지된다.
    }
  }, []);

  const toggle = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed, setCollapsed]);

  return { collapsed, setCollapsed, toggle };
}

/**
 * 이 방에서 판정기 배지가 **실제로 도는가** — 접기 손잡이를 세울지의 판정.
 *
 * 배지 없이 접히면 "이유 없이 사라진 줄" 이 된다. 판정기·배지가 꺼진 방에
 * 손잡이만 서 있으면 그것이 **거짓 손잡이**다(`main.py:858 _judge_badge_on`
 * 과 같은 규율 — 서버는 방 설정으로, 화면은 실제로 도착한 배지로 판정한다).
 *
 * 서버 설정은 와이어에 없으므로 **도착한 자막**을 본다: 한 건이라도
 * `assistant_judge` 사유를 달고 오면 그 방은 배지가 도는 방이다.
 */
export function hasAssistantJudgeItems(items: readonly FeedItem[]): boolean {
  for (const it of items) {
    if (it.kind !== "final") continue;
    const codes = it.data.review_reason ?? "";
    if (codes.split(",").includes("assistant_judge")) return true;
  }
  return false;
}
